"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.languageCache = exports.LanguageCache = void 0;
const intl_messageformat_1 = require("intl-messageformat");
const intl_1 = require("@formatjs/intl");
const constants_js_1 = require("./constants.js");
const helpers_js_1 = require("./helpers.js");
const JsonLoader_js_1 = require("./JsonLoader.js");
const formatters_js_1 = require("./formatters.js");
const store_js_1 = require("./store.js");
class LanguageCache {
    get currentTranslation() {
        const locale = this.currentLocale;
        return (this.cache[locale] ??
            this.cache[(0, helpers_js_1.extractLanguage)(locale)] ??
            this.cache[constants_js_1.DEFAULT_LOCALE]);
    }
    // eslint-disable-next-line class-methods-use-this
    get currentLocale() {
        return store_js_1.useStore.getState().locale;
    }
    cache = {};
    translationLoader = new JsonLoader_js_1.JSONLoader();
    intlCache = (0, intl_1.createIntlCache)();
    initialized = false;
    injectLoaders = (translationLoaders) => {
        const defaultLanguageLoader = translationLoaders[constants_js_1.DEFAULT_LOCALE] ||
            translationLoaders[(0, helpers_js_1.extractLanguage)(constants_js_1.DEFAULT_LOCALE)];
        if (defaultLanguageLoader === undefined) {
            throw new TypeError(`Default language "${constants_js_1.DEFAULT_LOCALE}" should be defined`);
        }
        if (typeof defaultLanguageLoader === 'function') {
            throw new TypeError(`Default language "${constants_js_1.DEFAULT_LOCALE}" should not be lazy loaded`);
        }
        this.translationLoader.injectLoaders(translationLoaders);
        void this.loadMissingMessages(this.currentLocale);
        // Warm up the translation cache with all loaded languages
        this.translationLoader.forEachLoaded((messages, locale) => {
            this.assignCache(messages, locale);
        });
        if (!this.initialized) {
            this.initialized = true;
            store_js_1.store.initialize(this.createTranslator(), this.createFormatters());
        }
    };
    changeLocale = (locale) => {
        store_js_1.store.change(locale, this.createTranslator(), this.createFormatters());
        document.documentElement.lang = locale;
        try {
            localStorage.setItem(constants_js_1.LOCAL_STORAGE_KEY_LOCALE, locale);
        }
        catch {
            // When in private mode, localStorage can throw
        }
    };
    loadMissingMessages = async (locale) => {
        const messages = await this.translationLoader.get(locale);
        this.assignCache(messages, locale);
        this.changeLocale(locale);
    };
    assignCache = (messages, locale) => {
        if (this.cache[locale] == null) {
            const intlFormatters = (0, intl_1.createIntl)({
                locale,
                messages: {},
            }, this.intlCache);
            this.cache[locale] = {
                translationCache: {},
                formatters: this.getFormatters(intlFormatters),
            };
        }
        for (const key of Object.keys(messages)) {
            if (this.cache[locale].translationCache[key] == null) {
                this.cache[locale].translationCache[key] = new intl_messageformat_1.IntlMessageFormat(messages[key], locale, constants_js_1.formats);
            }
        }
    };
    loadTranslations = async (locale = constants_js_1.DEFAULT_LOCALE) => {
        if (!this.translationLoader.exists(locale)) {
            throw new Error(`${locale} locale doesn't exist`);
        }
        store_js_1.store.startLoading();
        try {
            const messages = await this.translationLoader.get(locale);
            this.assignCache(messages, locale);
        }
        catch (error) {
            store_js_1.store.setError(error instanceof Error ? error.message : String(error));
            throw error;
        }
        store_js_1.store.stopLoading();
        return this.cache[locale].translationCache;
    };
    set = async (locale) => {
        // Fetch remote translations and compile
        // If the locale is not found we try the short version eg. "en-US" -> "en"
        try {
            const translation = await this.loadTranslations(locale);
            this.changeLocale(locale);
            return translation;
        }
        catch {
            console.warn(`Could not set language for "${locale}" trying fallback "${constants_js_1.DEFAULT_LOCALE}"`);
            // Fallback to default locale
            // TODO: Check browser if we have a better match
            await this.set(constants_js_1.DEFAULT_LOCALE);
        }
    };
    getTranslation = (key) => {
        return (this.currentTranslation?.translationCache[key] ??
            this.cache[constants_js_1.DEFAULT_LOCALE].translationCache[key]);
    };
    translate = (key, values) => {
        const translation = this.getTranslation(key);
        if (translation == null) {
            return `[${key}]`;
        }
        try {
            return translation.format(values);
        }
        catch (error) {
            if (error instanceof Error) {
                console.error(error.message);
            }
            return `[${key}]`;
        }
    };
    keyExists = (key) => {
        return this.getTranslation(key) != null;
    };
    createTranslator = () => {
        const referenceObject = this.cache[this.currentLocale] ||
            this.cache[(0, helpers_js_1.extractLanguage)(this.currentLocale)] ||
            this.cache[constants_js_1.DEFAULT_LOCALE];
        if (referenceObject == null) {
            throw new Error('Cannot create translator before translation loader is configured.');
        }
        return new Proxy({}, {
            get: (_target, name) => {
                if (typeof name !== 'string') {
                    return undefined;
                }
                return (values) => this.translate(name, values);
            },
        });
    };
    getFormatters = (intlFormatters) => {
        return (0, formatters_js_1.getFormatters)(intlFormatters, this.translate);
    };
    createFormatters = () => {
        const currentLocaleCache = this.cache[this.currentLocale];
        if (currentLocaleCache && currentLocaleCache.formatters) {
            return currentLocaleCache.formatters;
        }
        const intlFormatters = (0, intl_1.createIntl)({
            locale: constants_js_1.DEFAULT_LOCALE,
            messages: {},
            formats: constants_js_1.formats,
        }, this.intlCache);
        return this.getFormatters(intlFormatters);
    };
}
exports.LanguageCache = LanguageCache;
exports.languageCache = new LanguageCache();
//# sourceMappingURL=LanguageCache.js.map